<template>
     <SubLayout
        :pageTitle="title"
        :pageBreadcrumbs="breadcrumbs"
        :hasBack="false"
        :loading="false"
    > 
        <template #subHeaderActions>
            <div class="d-flex flex-wrap align-items-center justify-content-end">
                <Button  v-if="$can(abilities.UPDATE_ESTIMATE) || $can(abilities.UPDATE_ALL_ESTIMATE)" btn-class="me-2 mb-2 btn-sm" v-show="estimate?.status == 'DRAFT' || estimate?.status == 'PENDING'" @click="editDevis" type="new" btn-text="crm.devis.edit" />
                <Button v-if="$can(abilities.SEND_ESTIMATE)" btn-class="me-2 mb-2 btn-sm" v-show="estimate?.status == 'DRAFT'" @click.prevent="openSendModal" type="new" :btn-text="(estimate?.status == 'DRAFT' && estimate?.sent >= 1) ? 'crm.devis.relaunch' : 'crm.devis.send'" />
                <Button  v-if="$can(abilities.UPDATE_ESTIMATE) || $can(abilities.UPDATE_ALL_ESTIMATE)" btn-class="me-2 mb-2 btn-sm" v-show="estimate?.status == 'DRAFT' || estimate?.status == 'PENDING'" @click="openValidateModal" type="submit" btn-text="crm.devis.validate" />
                <ButtonDropdown
                    :btn-title="$t('label.change_status')"
                    :search="false"
                    :options="Object.values(filterStatus)"
                    value-field="value"
                    :name-field="locale"
                    :show-icon="false"
                    v-model="status"
                    btn-class="me-2 mb-2 btn-sm"
                    v-show="estimate?.status !== 'REJECTED' && estimate?.status !== 'CANCELLED'"
                    v-if="$can(abilities.UPDATE_ESTIMATE) || $can(abilities.UPDATE_ALL_ESTIMATE)"
                />
                <Button icon-class="fas fa-backspace" btn-class="btn-sm mb-2" type="cancel" btn-text="crm.back" @click="router.go(-1)"/>
            </div>
        </template>
        <template #layoutContent>
          <Loader :isLoading="isLoading">
            <div class="row d-flex flex-wrap px-5">
                <div class="card p-5 mb-4 me-md-5 flex-grow-1 col-12 col-md-3 col-lg-3">
                    <Button v-if="$canAccess(moduleAbilities.CRM_INVOICE) && $can(abilities.CREATE_INVOICE)" btn-class="me-3 mb-5 btn-sm" v-show="estimate?.status == 'APPROVED' && !estimate?.invoice_id" @click="generateInvoice" icon-class="fas fa-print" type="new" height="45px" btn-text="crm.devis.generate_facture"></Button>
                    <div class="card col-12 shadow mb-5 p-5" v-show="estimate?.invoice_id" v-if="$can(abilities.VIEW_INVOICE) || $can(abilities.VIEW_ALL_INVOICE)">
                        <div class="d-flex align-items-center flex-row-fluid flex-wrap w-100">
                            <div class="d-flex flex-grow-1">
                                <h5 class="me-2 text-nowrap">{{ $t('invoices.invoice') }} N° </h5>
                                <div class="text-nowrap">{{ estimate?.invoice_number }}</div>
                            </div>
                            <router-link :to="{name: 'invoices-view', params: { id: estimate?.invoice_id } }" class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary w-30px h-30px">
                                <i class="fas fa-eye"></i>
                            </router-link>
                        </div>
                    </div>
                    <div class="card col-12 shadow mb-5 p-5" v-if="estimate?.opportunity_id" v-show="$canAccess(moduleAbilities.CRM_PIPELINE) && ($can(abilities.VIEW_OPPORTUNITIES) || $can(abilities.VIEW_ALL_OPPORTUNITIES))">
                        <div class="d-flex align-items-center flex-row-fluid flex-wrap w-100">
                            <div class="d-flex flex-grow-1">
                                <h5 class="me-2 text-nowrap">{{ $t('crm.pipeline.label.name') }} : </h5>
                                <div class="text-nowrap">{{ estimate?.opportunity_name.length > 20 ? estimate?.opportunity_name.substring(0, 20) + '...' : estimate?.opportunity_name  }}</div>
                            </div>
                            <router-link :to="{name: 'pipelines-view', params: { id: estimate?.opportunity_id } }" class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary w-30px h-30px">
                                <i class="fas fa-eye"></i>
                            </router-link>
                        </div>
                    </div>

                    <div class="card mb-5 col-12 shadow p-5">
                        <div class="d-flex justify-content-between py-5">
                            <div>{{ $t('devis.paidAmount') }}</div>
                            <div>{{$formatLocalAmount(estimate?.total)}} {{ currency }}</div>
                        </div>
                    </div>

                    <div class="card shadow-sm mb-5 p-5" v-show="estimate.notes">
                        <div class="d-flex">
                            <h5 class="me-5 mb-3">{{ $t('crm.estimate.label.addNote') }} </h5>
                        </div>
                        <p>{{ estimate?.notes }}</p>
                    </div>
                </div>
                <div class="card card-px-0 px-0 flex-grow-3 flex-md-grow-1 col-12 col-md-3 col-lg-3" style="flex: 1 1 60%;">
                    <Show :estimate="estimate" :currency="currency"/>
                    <div class="card-toolbar">
                        <AlertModal v-model:showingAlert="showingAlert" @cancel="cancelSend"/>
                    </div>
                </div>
            </div>
          </Loader>
        </template>
    </SubLayout>
    <SendModal id="sendModal" @submit="submitSend" type="ESTIMATE" :content="emailFormData" :is-loading="isLoadingSend" :title="$t('devis.send')" :file_number="$t('devis.devis') +' '+ estimate.estimate_number"/>
    <SendModal id="relauchModal" @submit="submitSend" type="UNVALIDATED_ESTIMATE" :content="emailFormData" :is-loading="isLoadingSend" :title="$t('devis.relaunch')" :file_number="$t('devis.devis') +' '+ estimate.estimate_number"/>
</template>

<script setup>
import { ref, onMounted, computed, watch, shallowRef} from "vue";
import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
import { useRouter, useRoute } from 'vue-router';
import Loader from "@/components/Loader";
import Button from "@/components/Button";
import Show from "./components/Show.vue"
import SendModal from "@/Modules/CRM/components/Modals/SendModal.vue"
import {useSwal} from "@/composables/useSwal";
import {useStore} from "vuex";
import i18n from "@/plugins/i18n";
import {$formatLocalAmount} from "@/mixins/Helpers";
import AlertModal from '@/Modules/CRM/components/Modals/AlertModal.vue';
import { useLocales } from "@/composables/locales";
import ButtonDropdown from "@/components/ButtonDropdown.vue";
import abilities from "@/composables/abilities";
import moduleAbilities from "@/composables/moduleAbilities";

const {locale} = useLocales()
const store = useStore()
const router = useRouter()
const route = useRoute()

const {showSwal} = useSwal()

const isLoading = ref(false)
const title = ref( "crm.devis.title")

const showingAlert = ref(false)

const breadcrumbs = ref([
  {
    name: 'estimate.list',
    route: 'estimates',
    params: {}
  },
  {
    name: 'crm.devis.detail',
    route: 'estimates-view',
    params: route.params.id
  }
])

const emailFormData = shallowRef({
  estimateId: route.params.id,
  body: "",
  subject: i18n.global.t('devis.devis'),
  facture: "",
  to: [],
  from: ''
})

const modalId = ref('')

const isLoadingSend = ref(false)
const status = ref('')

const estimate = computed(() => store.getters['estimates/currentEstimate'])
const currency = computed(() => estimate.value?.currency?.symbol)
const company = computed(() => store.getters['company/getCompanyInfos'])
const estimateStatus = computed(() => store.getters['estimates/estimateStatus'])
const estimateStatusWithoutAll = computed(() => estimateStatus.value.filter(status => status.value !== 'all'))
const filterStatus = computed(() => {
    switch (estimate.value?.status) {
        case 'DRAFT':
          return estimateStatusWithoutAll.value.filter(status => status.value !== 'DRAFT');
        case 'PENDING':
          return estimateStatusWithoutAll.value.filter(status => status.value !== 'DRAFT' && status.value !== 'PENDING');
        case 'APPROVED':
          return estimateStatusWithoutAll.value.filter(status => status.value !== 'DRAFT' && status.value !== 'PENDING' && status.value !== 'APPROVED');
        default:
          return estimateStatusWithoutAll.value;
    }
})

watch(() => company.value, () => {
    emailFormData.value.from = company.value?.society?.society_email
})

watch(() => estimate.value, (value) => {
    if(value){
        emailFormData.value.to = [estimate.value?.contact?.email]
    }
})

watch(() => status.value, () => {
    store.dispatch('estimates/changeEstimateStatus', {estimate_id: route.params.id, status: status.value})
        .then(() => {
            window.toastr['success'](i18n.global.t('notification.status_changed'))
            loadEstimate()
        })
        .catch(() => {

        })
})

function editDevis(){
    router.push({ name: 'estimates-edit', params: {id: route.params.id} })
}

function openValidateModal(){
    showSwal(
        i18n.global.t('crm.estimate.swal.validate'),
        i18n.global.t('crm.estimate.swal.validate_text')
    ).then( async (result) => {
        if (result.value) {
            store.dispatch('estimates/validateEstimate', {estimate_id: route.params.id})
                .then(() => {
                    window.toastr['success'](i18n.global.t('notification.estimate_validated'))
                    loadEstimate()
                    closeModal()
                })
                .catch(() => {
                    
                })
        }
    })
}

function cancelSend(){
    store.dispatch('estimates/cancelEstimateMail',route.params.id)
        .then(() => {
            window.toastr['success'](i18n.global.t('notification.mail_cancelled'))
            showingAlert.value = false;
        })
        .catch(() => {
            showingAlert.value = false;
        })
}

function generateInvoice(){
    showSwal(
        i18n.global.t('crm.estimate.swal.generate'),
        i18n.global.t('crm.estimate.swal.generate_text')
    ).then( async (result) => {
        if (result.value) {
            store.dispatch('estimates/generateInvoice', {estimate_id: route.params.id})
                .then(() => {
                    window.toastr['success'](i18n.global.t('notification.invoice_generated'))
                    loadEstimate()
                })
                .catch(() => {
                    
                })
        }
    })
}

function openSendModal(){
    if(estimate.value?.sent >= 1 && estimate.value?.status == 'DRAFT'){
        $('#relauchModal').modal('toggle')
        modalId.value = '#relauchModal'
    }
    if(estimate.value?.sent == 0 && estimate.value?.status == 'DRAFT'){
        $('#sendModal').modal('toggle')
        modalId.value = '#sendModal'
    }
}

function submitSend () {
    isLoadingSend.value = true
    store.dispatch('estimates/sendEstimate', emailFormData.value)
        .then(() => {
            $(modalId.value).modal('toggle');
            loadEstimate()
            emailFormData.value.to = [estimate.value?.contact?.email]
            window.toastr['success'](i18n.global.t('notification.estimate_send'))
            isLoadingSend.value = falsesubmit
            alertDiv()
        })
        .catch(() => {
            isLoadingSend.value = false
        })
}

function loadEstimate(){
    store.dispatch('estimates/getEstimateById', route.params.id)
}

function loadData () {
  Promise.all([
    loadEstimate(),
    store.dispatch('payments/loadPaymentTypes'),
    store.dispatch('estimates/loadEstimateStatus'),
    store.dispatch('company/generalInformation')
  ])
}

onMounted(() => {
  loadData()
  store.dispatch('mail/loadMailMessage')
})

function alertDiv(){
    showingAlert.value = true;
    $('#sendModal').modal('toggle')
    setTimeout(() => {
        showingAlert.value = false;
      }, 5000);
}
</script>

<style scoped>
    .card-parent {
        position: relative;
    }

    .card-child {
        position: absolute;
        bottom: 0;
        width: 90%;
        font-weight: 500;
        margin-bottom: 20px;
    }
    .shadow {
        box-shadow: 0 0.1rem 0.5rem 0.1rem rgba(0, 0, 0, .075) !important;
    }
</style>